import React, { useState } from "react";
import { Mail, Linkedin } from "lucide-react";

// Toggle nav items — add/remove entries here to change the tabs.
const SECTIONS = [
  { id: "about", label: "About" },
  { id: "research", label: "Research" },
  { id: "teaching", label: "Teaching" },
  { id: "service", label: "Service" },
  { id: "creative-work", label: "Creative Work" },
];

export default function PersonalWebsite() {
  const [activeSection, setActiveSection] = useState("about");

  // Education history
  const educationEntries = [
    {
      institution: "Stanford University",
      degree: "Ph.D. Candidate, Environmental Engineering",
      dates: "2024 – Present",
      details: ["Advisor: Dr. Alexandria Boehm"],
    },
    {
      institution: "Stanford University",
      degree: "M.S. Environmental Engineering (Dual Degree); GPA: 4.04",
      dates: "2024 – 2026",
      details: ["Focus: Human and Environmental Health"],
    },
    {
      institution: "University of California, Los Angeles (UCLA)",
      degree: "B.S. Civil Engineering; GPA: 3.93",
      dates: "2020 – 2024",
      details: ["Cum Laude, Focus in Environmental Health"],
    },
  ];

  // Teaching awards
  const teachingAwards = [
    {
      title: "2026 Stanford Centennial Teaching Assistant Award",
      url: "https://ctl.stanford.edu/grad-teaching/ta-award/winners",
      description:
        "One of Stanford University's highest honors for graduate student teaching excellence, awarded on a biennial basis.",
    },
  ];

  // Teaching history
  const teachingEntries = [
    {
      course: "CEE 275: California Coast: Science, Policy, and Law",
      term: "Spring 2026",
    },
    {
      course: "CEE 274P: Environmental Health Microbiology Lab",
      term: "Fall 2025",
    },
  ];

  // Service panels data (outreach + creative work)
  const servicePanels = [
    {
      title: "Society of Women Engineers (SWE)",
      titleUrl: "https://swe.org/",
      segments: [
        {
          dates: "2025 - Present",
          role: "DEI Advisory Council",
          description: `Member of SWE nation-wide council of 15 stakeholders providing strategic guidance and support to collegiate sections and affiliates who are navigating legislation changes and associated restrictions.`,
          link: {
            url: "https://alltogether.swe.org/2026/09/2026-swe-awards-recipients/#outstanding-graduate",
            buttonLabel: "2026 Outstanding Graduate Student Awardee",
          },
        },
        {
          dates: "2024-Present",
          role: "Public Policy Affinity Group Co-lead",
          description: `I coordinated national conference programming, Postcards for Policy. Here, attendees learned about legislative processes, advocacy topics of interest, and bridged these into a writing session to their elected representatives. I also led the development of civic engagement and voter registration content for national SWE social media for the 2024 general election. In addition, I have been a writing contributor to the SWE national newsletter.`,
        },
        {
          dates: "2020-2024",
          role: "UCLA Collegiate Section",
          description: `I am proud to have been the co-founder and two-term director of the SWE@UCLA Lobbying Committee. Our mission was to connect UCLA engineers to science-policy initiatives, explore pathways to contribute to policymaking from a technical background, and directly visit with representatives in Washington D.C. to engage in constituent advocacy and learn about engineers in policy careers.`,
        },
      ],
      images: ["../presentation_swe.jpeg", "../swe_picture.jpeg"],
      links: [
        {
          url: "https://alltogether.swe.org/2024/06/sweuclas-passion-for-public-policy/",
          buttonLabel: "SWE Blog Article",
        },
      ],
    },
    {
      title: "Stanford Asian American Activities Center (A3C)",
      segments: [
        {
          dates: "2026 - Present",
          role: "Graduate Life Coordinator",
          description: `So grateful to serve as a part of the student staff team in the A3C! In this role, I uplift the Stanford Asian American community through hosting events focused on community-building, intersectional awareness with other on-campus organizations, and uplifting Stanford resources to support student mental health and wellbeing.`,
        },
      ],
      images: ["../AAGSA_Cards.JPG"],
    },
    {
      title: "Stanford Doerr School Access, Belonging, Community (ABC) Center",
      segments: [
        {
          dates: "2024-Present",
          role: "Recruitment Ambassador",
        },
        {
          dates: "2025-2026",
          role: "ABC Liaison",
        },
      ],
    },
  ];

  // Creative Work & Art panels
  const creativeWorkPanels = [
    {
      title: "Lion Dance",
      content: `Outside of the lab, I also love to perform lion dance! For those unfamiliar, lion dance is an ancient folk dance originating in China, but now present across many regions in East and Southeast Asia. Through performance as a pair inside a lion costume, often at new year celebrations, weddings, and birthdays, we bring good luck and blessings to all! Through this dance, I celebrate my heritage as a mixed Chinese-American student and uplift our cultural art to a broader audience! I currently perform on Stanford's Lion Dance team and serve as the team's social chair. My leadership on UCLA's team during my undergraduate is featured in the below LAist article & NBC interview.`,
      image: "../nbc_lion_dance_interview.jpg",
      links: [
        {
          url: "https://laist.com/brief/news/arts-and-entertainment/what-it-takes-to-be-a-lion-dancer-on-lunar-new-year-in-la",
          buttonLabel: "NPR LAist Article",
        },
        {
          url: "https://www.nbclosangeles.com/video/california-live/meet-the-students-behind-uclas-lion-dance-troupe/3334615/",
          buttonLabel: "NBC Interview & Feature",
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-peach-50 to-amber-50">
      {/* Masthead — decorative background banner only, no text/content */}
      <section className="h-44 md:h-72 relative overflow-hidden">
        <img
          src="../poppies.JPG"
          alt="Background"
          className="w-full h-full object-cover object-[center_60%]"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-orange-900/40 via-peach-900/30 to-amber-900/40"></div>
      </section>

      {/* Toggle Nav — clicking swaps the panel below, no scrolling */}
      <nav className="sticky top-0 bg-white/90 backdrop-blur-md border-b border-orange-100 z-50 shadow-sm">
        <div className="max-w-5xl mx-auto px-6 py-4">
          <ul className="flex flex-wrap justify-center gap-2 md:gap-3">
            {SECTIONS.map((section) => (
              <li key={section.id}>
                <button
                  onClick={() => setActiveSection(section.id)}
                  className={`px-5 py-2 rounded-full text-sm md:text-base font-medium tracking-wide uppercase transition-all duration-300 ${
                    activeSection === section.id
                      ? "bg-orange-500 text-white shadow-md"
                      : "text-slate-700 hover:bg-orange-50 hover:text-orange-600"
                  }`}
                  aria-pressed={activeSection === section.id}
                >
                  {section.label}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </nav>

      {/* Tab Panel — only the active section renders */}
      <main className="max-w-6xl mx-auto px-6 py-16 md:py-20 min-h-[50vh]">
        {activeSection === "about" && (
          <section key="about" id="about" className="animate-fade-in">
            <div className="flex flex-col md:flex-row md:items-start gap-10 md:gap-16">
              {/* Identity column */}
              <div className="flex flex-col items-center text-center md:w-2/5 flex-shrink-0">
                <div className="w-56 h-56 md:w-64 md:h-64 mb-6 rounded-full overflow-hidden ring-4 ring-white shadow-xl">
                  <img
                    className="w-full h-full object-cover"
                    src="../Cropped_LK.jpg"
                    alt="Laura smiling at the camera wearing a light pink qipao. Her hair is dark brown and shoulder length."
                  />
                </div>
                <h3
                  className="text-2xl text-slate-800 mb-1"
                  style={{ fontFamily: '"Nunito", sans-serif' }}
                >
                  Laura Kubiatko
                </h3>
                <p className="italic text-slate-500 text-base mb-2">
                  she/her/hers
                </p>
                <p className="text-slate-500 text-base font-light mb-4">
                  Ph.D. Candidate
                  <br />
                  Stanford Environmental Engineering
                  <br />
                  
                </p>
                <div className="flex gap-5 justify-center items-center">
                  <a
                    href="mailto:laurakubiatko@gmail.com"
                    className="flex items-center gap-2 text-slate-600 hover:text-orange-600 transition-all duration-300 hover:scale-110"
                    aria-label="Email Laura"
                  >
                    <Mail className="w-5 h-5" />
                  </a>
                  <a
                    href="https://linkedin.com/in/laurakubi"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-slate-600 hover:text-orange-600 transition-all duration-300 hover:scale-110"
                    aria-label="LinkedIn Profile"
                  >
                    <Linkedin className="w-5 h-5" />
                  </a>
                  <a
                    href="https://orcid.org/0009-0005-6984-4900"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-slate-600 hover:text-orange-600 transition-all duration-300 hover:scale-110"
                    aria-label="ORCID Profile"
                  >
                    <svg
                      className="w-5 h-5"
                      viewBox="0 0 256 256"
                      fill="currentColor"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M128 0C57.3 0 0 57.3 0 128s57.3 128 128 128 128-57.3 128-128S198.7 0 128 0zM86.3 186.2H70.9V79.1h15.4v107.1zM78.6 66.5c-5.5 0-9.9-4.5-9.9-9.9 0-5.5 4.5-9.9 9.9-9.9 5.5 0 9.9 4.5 9.9 9.9 0 5.4-4.4 9.9-9.9 9.9zM224.9 186.2h-15.3v-52.5c0-12.5-4.5-21-15.8-21-8.6 0-13.7 5.8-16 11.4-.8 2-1 4.8-1 7.6v54.5h-15.3s.2-98.5 0-107.1h15.3v15.2c2-3.5 5.6-8.5 13.7-12.3 8.5-4 19.6-4.3 29.2 1.2 10.8 6.1 15.2 16.8 15.2 31.2v71.8z" />
                    </svg>
                  </a>
                </div>
              </div>

              {/* Bio column */}
              <div className="flex-1">
                <div className="prose prose-lg prose-slate max-w-none space-y-6 text-slate-700 leading-relaxed">
                  <p
                    className="text-lg md:text-xl text-justify"
                    style={{ fontFamily: '"Nunito", sans-serif' }}
                  >
                    Hey! I'm Laura, an Environmental Engineering PhD Candidate
                    at Stanford University in the{" "}
                    <a
                      href="https://web.stanford.edu/~aboehm/website.htm"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-orange-600 hover:text-orange-700 underline decoration-orange-300 hover:decoration-orange-500 transition-colors"
                      style={{ fontFamily: '"Nunito", sans-serif' }}
                    >
                      Boehm Research Group
                    </a>
                    .
                  </p>
                  <p
                    className="text-lg md:text-xl text-justify"
                    style={{ fontFamily: '"Nunito", sans-serif' }}
                  >
                    My current research focuses on leveraging
                    environmental DNA (eDNA) as a tool for marine
                    biomonitoring. eDNA refers to the genetic
                    material that organisms shed into their
                    environments! We can collect and extract this DNA from 
                    matrices like water or sediment and analyze
                    it using molecular techniques and bioinformatics. This can 
                    give us some insight into community composition 
                    or species-specific presence!
                  </p>
                  <p
                    className="text-lg md:text-xl text-justify"
                    style={{ fontFamily: '"Nunito", sans-serif' }}
                  >
                    Recently, I have been collaborating with
                    the Sunflower Star Laboratory
                    (SSL), to advance our applications of rare species detection in coastal environments
                    using eDNA paired with ocean hydrodynamics, specifically working with 
                     critically endangered Sunflower stars (Pycnopodia helianthoides).
                    Read more about the Boehm group's collaborations with SSL{" "}
                    <a
                      href="https://www.sfgate.com/national-parks/article/california-sunflower-stars-successfully-released-21259526.php"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-orange-600 hover:text-orange-700 underline decoration-orange-300 hover:decoration-orange-500 transition-colors"
                      style={{ fontFamily: '"Nunito", sans-serif' }}
                    >
                      here in SF Gate!
                    </a>
                    !
                  </p>
                </div>
              </div>
            </div>

            {/* Education — full width, no longer sharing the row with the photo */}
            <div className="mt-14 max-w-4xl mx-auto">
              <h3 className="text-2xl md:text-3xl text-slate-800 mb-6">
                Education
              </h3>
              <div className="divide-y divide-orange-300">
                {educationEntries.map((edu, index) => (
                  <div key={index} className="py-4">
                    <div className="flex flex-col md:flex-row md:items-baseline md:justify-between gap-1">
                      <h4 className="text-2xl font-bold text-slate-800">
                        {edu.institution}
                      </h4>
                      <span className="text-sm text-slate-500">
                        {edu.dates}
                      </span>
                    </div>
                    <p className="text-slate-700">{edu.degree}</p>
                    {edu.details.map((line, i) => (
                      <p key={i} className="text-slate-500 text-sm">
                        {line}
                      </p>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* Media */}
            <div className="mt-14 max-w-4xl mx-auto">
              <h3 className="text-2xl md:text-3xl text-slate-800 mb-6">
                Media &amp; Features
              </h3>
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-8 md:p-10 border border-orange-200 text-left">
                <ul className="list-disc list-inside space-y-2">
                  <li>
                    <a
                      href="https://engineering.stanford.edu/spotlight/laura-kubiatko?utm_campaign=feed&utm_medium=referral&utm_source=later-linkinbio"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-bold text-slate-700 underline decoration-slate-400 hover:text-orange-600 hover:decoration-orange-400 transition-colors"
                    >
                      2026 Stanford Engineering Spotlight
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://alltogether.swe.org/2024/06/sweuclas-passion-for-public-policy/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-700 underline decoration-slate-400 hover:text-orange-600 hover:decoration-orange-400 transition-colors"
                    >
                      2024 SWE Feature
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://laist.com/brief/news/arts-and-entertainment/what-it-takes-to-be-a-lion-dancer-on-lunar-new-year-in-la"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-700 underline decoration-slate-400 hover:text-orange-600 hover:decoration-orange-400 transition-colors"
                    >
                      2024 NPR LAist Lion Dance Interview
                    </a>
                  </li>
                  <li>
                    <a
                      href="https://www.nbclosangeles.com/video/california-live/meet-the-students-behind-uclas-lion-dance-troupe/3334615/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-slate-700 underline decoration-slate-400 hover:text-orange-600 hover:decoration-orange-400 transition-colors"
                    >
                      2024 NBC Lion Dance Interview
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </section>
        )}

        {activeSection === "research" && (
          <section
            key="research"
            id="research"
            className="max-w-4xl mx-auto animate-fade-in"
          >
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-8 md:p-10 border border-orange-200 text-center">
              <p className="text-slate-700 leading-relaxed">
                Section currently in progress! You can check out the{" "}
                <a
                  href="https://web.stanford.edu/~aboehm/website.htm"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-orange-600 hover:text-orange-700 underline decoration-orange-300 hover:decoration-orange-500 transition-colors"
                >
                  Boehm Research Group
                </a>{" "}
                page, or feel free to reach out to me via email! 
              </p>
            </div>

            <div className="mt-8 rounded-lg overflow-hidden border border-orange-200 max-w-2xl mx-auto">
              <img
                src="../ssl.JPG"
                alt="Laura and a labmate holding mesh bags of oyster shells in front of the Sunflower Star Laboratory sign"
                className="w-full h-auto"
              />
            </div>
          </section>
        )}

        {activeSection === "teaching" && (
          <section
            key="teaching"
            id="teaching"
            className="max-w-4xl mx-auto animate-fade-in"
          >
            <div>
              <h3 className="text-2xl md:text-3xl text-slate-800 mb-6">
                Teaching Awards
              </h3>
              <div className="divide-y divide-orange-300">
                {teachingAwards.map((award, index) => (
                  <div key={index} className="py-4">
                    {award.url ? (
                      <a
                        href={award.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-2xl font-bold text-slate-800 hover:text-orange-600 underline decoration-orange-300 hover:decoration-orange-500 transition-colors"
                      >
                        {award.title}
                      </a>
                    ) : (
                      <h4 className="text-2xl font-bold text-slate-800">
                        {award.title}
                      </h4>
                    )}
                    <p className="text-base text-slate-500 mt-1">
                      {award.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-14">
              <h3 className="text-2xl md:text-3xl text-slate-800 mb-6">
                Courses
              </h3>
              <div className="divide-y divide-orange-300">
                {teachingEntries.map((entry, index) => (
                  <div key={index} className="py-4">
                    <div className="flex flex-col md:flex-row md:items-baseline md:justify-between gap-1">
                      <h4 className="text-2xl font-bold text-slate-800">
                        {entry.course}
                      </h4>
                      <span className="text-lg text-slate-500">
                        {entry.term}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {activeSection === "service" && (
          <section
            key="service"
            id="service"
            className="max-w-4xl mx-auto animate-fade-in"
          >
            {/* Header list, matching the Teaching tab style */}
            <div className="divide-y divide-orange-300">
              {servicePanels.map((panel, index) => (
                <div key={index} className="py-6">
                  {panel.titleUrl ? (
                    <a
                      href={panel.titleUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-2xl font-bold text-slate-800 hover:text-orange-600 underline decoration-orange-300 hover:decoration-orange-500 transition-colors"
                    >
                      {panel.title}
                    </a>
                  ) : (
                    <h4 className="text-2xl font-bold text-slate-800">
                      {panel.title}
                    </h4>
                  )}

                  {(panel.content || panel.segments) && (
                    <div className="mt-4 flex flex-col md:flex-row gap-6 items-stretch">
                      <div className="flex-1 flex flex-col justify-between space-y-4">
                        {panel.content && (
                          <p className="text-slate-700 leading-relaxed text-justify">
                            {panel.content}
                          </p>
                        )}

                        {panel.segments && (
                          <div className="space-y-5">
                            {panel.segments.map((segment, segIndex) => (
                              <div key={segIndex}>
                                <div className="flex flex-col md:flex-row md:items-baseline md:justify-between gap-1">
                                  <h5 className="font-semibold text-slate-800">
                                    {segment.role}
                                  </h5>
                                  <span className="text-sm text-slate-500 flex-shrink-0">
                                    {segment.dates}
                                  </span>
                                </div>
                                <p className="text-slate-700 leading-relaxed text-justify mt-1">
                                  {segment.description}
                                </p>
                                {segment.link && (
                                  <div className="mt-3">
                                    <a
                                      href={segment.link.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-block px-5 py-2 rounded-full text-sm font-medium bg-orange-300 text-slate-800 shadow-md hover:bg-orange-400 transition-all duration-300"
                                    >
                                      {segment.link.buttonLabel}
                                    </a>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Link Buttons (if any) */}
                        {panel.links && panel.links.length > 0 && (
                          <div className="flex flex-wrap gap-3">
                            {panel.links.map((linkItem, linkIndex) => (
                              <a
                                key={linkIndex}
                                href={linkItem.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block px-5 py-2 rounded-full text-sm font-medium bg-orange-300 text-slate-800 shadow-md hover:bg-orange-400 transition-all duration-300"
                              >
                                {linkItem.buttonLabel}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Multiple photos — stacked vertically, centered next to the text */}
                      {panel.images && (
                        <div className="md:w-64 flex-shrink-0 flex flex-col gap-4 md:self-center">
                          {panel.images.map((imgSrc, imgIndex) => (
                            <img
                              key={imgIndex}
                              src={imgSrc}
                              alt={panel.title}
                              className="w-full h-48 object-cover rounded-lg border border-orange-200"
                            />
                          ))}
                        </div>
                      )}

                      {/* Single photo (Lion Dance) — original stretched-to-text-height treatment */}
                      {panel.image && (
                        <div className="md:w-64 flex-shrink-0">
                          <img
                            src={panel.image}
                            alt={panel.title}
                            className="w-full h-full object-cover rounded-lg border border-orange-200"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {activeSection === "creative-work" && (
          <section
            key="creative-work"
            id="creative-work"
            className="max-w-4xl mx-auto animate-fade-in"
          >
            {/* Header list, matching the Service tab style */}
            <div className="divide-y divide-orange-300">
              <div className="py-6">
                <h4 className="text-2xl font-bold text-slate-800">
                  Illustrations
                </h4>
                <div className="mt-4 space-y-4">
                  <p className="text-slate-700 leading-relaxed text-justify">
                    I enjoy creating my own illustrations for presentations
                    and figures. I appreciate how these can be incredibly
                    effective at making technical or advanced concepts more
                    accessible to others. I hope to utilize illustrations as
                    a tool to advance my science communication skills! :)
                  </p>
                  <div
                    className="rounded-lg overflow-hidden border border-orange-200 max-w-md mx-auto select-none"
                    onContextMenu={(e) => e.preventDefault()}
                  >
                    <img
                      src="../animated.gif"
                      alt="Illustrated tide pool scene with DNA helix overlays"
                      className="w-full h-auto pointer-events-none"
                      draggable={false}
                    />
                  </div>
                  <p className="text-slate-700 leading-relaxed text-justify">
                    I also love to create logos and graphics for
                    organizations and clubs I'm a part of. We use these for
                    shirts, stickers, and for social media! See some of my
                    work below:
                  </p>
                  <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto">
                    {[
                      {
                        src: "../sdss_research.png",
                        alt: "SDSS Research Review 2026 logo",
                      },
                      {
                        src: "../esdei_logo.JPG",
                        alt: "Engineering Students 4 DEI logo",
                      },
                      {
                        src: "../lion_dance.png",
                        alt: "Stanford Lion Dance logo",
                      },
                    ].map((img, imgIndex) => (
                      <div
                        key={imgIndex}
                        className="aspect-square rounded-lg overflow-hidden border border-orange-200"
                      >
                        <img
                          src={img.src}
                          alt={img.alt}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {creativeWorkPanels.map((panel, index) => (
                <div key={index} className="py-6">
                  {panel.titleUrl ? (
                    <a
                      href={panel.titleUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-2xl font-bold text-slate-800 hover:text-orange-600 underline decoration-orange-300 hover:decoration-orange-500 transition-colors"
                    >
                      {panel.title}
                    </a>
                  ) : (
                    <h4 className="text-2xl font-bold text-slate-800">
                      {panel.title}
                    </h4>
                  )}

                  {(panel.content || panel.segments) && (
                    <div className="mt-4 flex flex-col md:flex-row gap-6 items-stretch">
                      <div className="flex-1 flex flex-col justify-between space-y-4">
                        {panel.content && (
                          <p className="text-slate-700 leading-relaxed text-justify">
                            {panel.content}
                          </p>
                        )}

                        {panel.segments && (
                          <div className="space-y-5">
                            {panel.segments.map((segment, segIndex) => (
                              <div key={segIndex}>
                                <div className="flex flex-col md:flex-row md:items-baseline md:justify-between gap-1">
                                  <h5 className="font-semibold text-slate-800">
                                    {segment.role}
                                  </h5>
                                  <span className="text-sm text-slate-500 flex-shrink-0">
                                    {segment.dates}
                                  </span>
                                </div>
                                <p className="text-slate-700 leading-relaxed text-justify mt-1">
                                  {segment.description}
                                </p>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Link Buttons (if any) */}
                        {panel.links && panel.links.length > 0 && (
                          <div className="flex flex-wrap gap-3">
                            {panel.links.map((linkItem, linkIndex) => (
                              <a
                                key={linkIndex}
                                href={linkItem.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block px-5 py-2 rounded-full text-sm font-medium bg-orange-300 text-slate-800 shadow-md hover:bg-orange-400 transition-all duration-300"
                              >
                                {linkItem.buttonLabel}
                              </a>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Multiple photos — stacked vertically, centered next to the text */}
                      {panel.images && (
                        <div className="md:w-64 flex-shrink-0 flex flex-col gap-4 md:self-center">
                          {panel.images.map((imgSrc, imgIndex) => (
                            <img
                              key={imgIndex}
                              src={imgSrc}
                              alt={panel.title}
                              className="w-full h-48 object-cover rounded-lg border border-orange-200"
                            />
                          ))}
                        </div>
                      )}

                      {/* Single photo — stretched to match the text column's height */}
                      {panel.image && (
                        <div className="md:w-64 flex-shrink-0">
                          <img
                            src={panel.image}
                            alt={panel.title}
                            className="w-full h-full object-cover rounded-lg border border-orange-200"
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-orange-100 to-amber-100 border-t border-orange-200 mt-32">
        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className="text-center space-y-4">
            <div className="flex gap-6 justify-center items-center">
              <a
                href="mailto:laurakubiatko@gmail.com"
                className="text-slate-700 hover:text-orange-600 transition-colors duration-300"
              >
                <Mail className="w-6 h-6" />
              </a>
              <a
                href="https://linkedin.com/in/laurakubi"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-700 hover:text-orange-600 transition-colors duration-300"
              >
                <Linkedin className="w-6 h-6" />
              </a>
            </div>
            <p className="text-slate-600 text-sm">
              © 2025. All rights reserved.
            </p>
          </div>
        </div>
      </footer>

      <style jsx>{`
        @import url("https://fonts.googleapis.com/css2?family=Nunito:wght@300;400;500;600;700;800&display=swap");

        * {
          font-family: "Nunito", sans-serif;
        }

        h1,
        h2,
        h3,
        h4,
        a {
          font-family: "Nunito", sans-serif;
        }

        .from-peach-50 {
          --tw-gradient-from: #fff5f0;
        }

        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .line-clamp-6 {
          display: -webkit-box;
          -webkit-line-clamp: 6;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fadeIn 0.35s ease-out;
        }
      `}</style>
    </div>
  );
}
